import React, { useState, useEffect } from 'react';
import { charactersAPI } from '../services/api';
import { Character } from '../types';
import CharacterCard from '../components/CharacterCard';
import CreateCharacterModal from '../components/CreateCharacterModal' ;
import CharacterSidePanel from '../components/CharacterSidePanel';
import ElementPreviewPanel from '../components/ElementPreviewPanel';

import '../styles/Characters.css';

const Characters: React.FC = () => {
  console.log('📊 Characters component rendering');
  
  const [characters, setCharacters] = useState<Character[]>([]);
  const [filteredCharacters, setFilteredCharacters] = useState<Character[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name-asc' | 'name-desc' | 'date-created' | 'date-created-oldest'>('date-created');
  
  // Global tab selection state
  const [globalTabSelection, setGlobalTabSelection] = useState<string>('summary');
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showElementPanel, setShowElementPanel] = useState(false);
  const [showElementPreview, setShowElementPreview] = useState(false);
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null);
  const [updatedCharacterIds, setUpdatedCharacterIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadCharacters();
  }, []);

  // Monitor characters array changes for debugging (only log significant changes)
  useEffect(() => {
    if (characters.length > 0) {
      console.log('🔄 Characters: Characters loaded:', {
        total: characters.length,
        withImageUrl: characters.filter(c => c.imageUrl).length
      });
    }
  }, [characters.length]);

  // Filter and sort characters whenever characters, searchTerm, or sortBy changes
  useEffect(() => {
    let filtered = [...characters];

    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(character =>
        character.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply sorting
    switch (sortBy) {
      case 'name-asc':
        filtered.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        filtered.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'date-created':
        filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        break;
      case 'date-created-oldest':
        filtered.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        break;
    }

    setFilteredCharacters(filtered);
  }, [characters, searchTerm, sortBy]);

  const loadCharacters = async () => {
    try {
      setLoading(true);
      console.log('🔄 Characters: Loading characters...');
      const data = await charactersAPI.getAll();
      console.log('✅ Characters: Characters loaded successfully:', data.length);
      setCharacters(data);
    } catch (error) {
      console.error('❌ Characters: Failed to load characters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCharacter = (updatedCharacter: Character) => {
    console.log('🔄 Characters: Updating character:', updatedCharacter._id);
    
    // Refresh all characters to ensure linked arrays are current
    const refreshAllCharacters = async () => {
      try {
        console.log('🔄 Characters: Refreshing all characters after update...');
        const refreshedCharacters = await charactersAPI.getAll();
        setCharacters(refreshedCharacters);
        console.log('🔄 Characters: All characters refreshed successfully');
      } catch (error) {
        console.error('🔄 Characters: Failed to refresh all characters:', error);
        // Fallback to updating just the specific character
        setCharacters(prev => 
          prev.map(char => 
            char._id === updatedCharacter._id ? updatedCharacter : char
          )
        );
      }
    };
    
    refreshAllCharacters();
    
    setUpdatedCharacterIds(prev => new Set([...Array.from(prev), updatedCharacter._id]));
    
    // Clear the updated flag after a delay
    setTimeout(() => {
      setUpdatedCharacterIds(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(updatedCharacter._id);
        return newSet;
      });
    }, 2000);
  };

  const handleDeleteCharacter = async (characterId: string) => {
    try {
      console.log('🔄 Characters: Deleting character:', characterId);
      await charactersAPI.delete(characterId);
      setCharacters(prev => prev.filter(char => char._id !== characterId));
      console.log('✅ Characters: Character deleted successfully');
      // Show success toast only after successful delete
      if (typeof window.showToast === 'function') {
        window.showToast('Character deleted successfully!', 'success');
      }
    } catch (error) {
      console.error('❌ Characters: Failed to delete character:', error);
      // Show error toast only on failure
      if (typeof window.showToast === 'function') {
        window.showToast('Failed to delete character. Please try again.', 'error');
      }
    }
  };

  const handleOpenSidePanel = (character: Character, field: string) => {
    console.log('🔄 Characters: Opening side panel:', { character: character._id, field });
    setSelectedCharacter(character);
    setShowElementPanel(true);
  };

  const handleOpenPreviewPanel = (elementIds: string[]) => {
    console.log('🔄 Characters: Opening preview panel for elements:', elementIds);
    setShowElementPreview(true);
  };

  const handleCloseSidePanel = () => {
    setShowElementPanel(false);
    setSelectedCharacter(null);
  };

  const handleClosePreviewPanel = () => {
    setShowElementPreview(false);
    setSelectedCharacter(null);
  };

  if (loading) {
    return (
      <div className="overview">
        <div className="loading">
          <p>Loading characters...</p>
        </div>
      </div>
    );
  }

    return (
    <>
      <div className="overview">
        {/* Header - Similar to Overview page */}
        <div className="overview-header">
          <h1 className="overview-title">Characters</h1>
          <p className="overview-subtitle">Manage your world's characters</p>
        </div>

        {/* Header with actions - similar to old overview */}
        <div className="overview-actions">
          <button 
            className="btn btn--green" 
            onClick={() => setShowCreateModal(true)}
          >
            Create New Character
          </button>
          <input
            type="text"
            placeholder="Search by name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="live-search"
          />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'name-asc' | 'name-desc' | 'date-created' | 'date-created-oldest')}
            className="sort-select"
          >
            <option value="date-created">Newest First</option>
            <option value="date-created-oldest">Oldest First</option>
            <option value="name-asc">A to Z</option>
            <option value="name-desc">Z to A</option>
          </select>
        </div>

        {/* Filter Section */}
        <div className="filter-section">
          {/* Left side - Filter dropdowns */}
          {/* <div className="filter-left">
            <button
              className="btn btn--clear-filters"
              onClick={() => {
                setSearchTerm('');
              }}
            >
              Clear Filters
            </button>
          </div> */}

          {/* Right side - Tab selection */}
          <div className="filter-right">
            <div className="filter-group">
              <label htmlFor="tab-filter" className="filter-label">Select Default Tab:</label>
              <select
                id="tab-filter"
                value={globalTabSelection}
                onChange={(e) => setGlobalTabSelection(e.target.value)}
                className="filter-select tab-select"
              >
                <option value="summary">Summary</option>
                <option value="features">Features</option>
                <option value="attributes">Attributes</option>
                <option value="equipment">Equipment</option>
                <option value="threads">Threads</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Card Container - Outside overview div */}
      <div className="card-container" id="results-container">
        {Array.isArray(filteredCharacters) && filteredCharacters.length > 0 ? (
          filteredCharacters.map(character => (
            <CharacterCard
              key={`${character._id}-${character.imageUrl || character.image || 'default'}-${updatedCharacterIds.has(character._id) ? 'updated' : 'stable'}`}
              character={character}
              onUpdate={handleUpdateCharacter}
              onDelete={handleDeleteCharacter}
              onOpenSidePanel={handleOpenSidePanel}
              onOpenPreviewPanel={handleOpenPreviewPanel}
              defaultTab={globalTabSelection}
            />
          ))
        ) : (
          <div className="no-results">
            <p>No characters found.</p>
          </div>
        )}
      </div>

      {/* Create Character Modal */}
      {showCreateModal && (
        <CreateCharacterModal
          onClose={() => setShowCreateModal(false)}
          onCharacterCreated={(newCharacter) => {
            setCharacters(prev => [newCharacter, ...prev]);
            setShowCreateModal(false);
          }}
        />
      )}

      {/* Side Panel */}
      {showElementPanel && selectedCharacter && (
        <CharacterSidePanel
          isOpen={showElementPanel}
          onClose={handleCloseSidePanel}
          character={selectedCharacter}
          field=""
          allElements={[]}
          onUpdate={handleUpdateCharacter}
        />
      )}

      {/* Element Preview Panel */}
      {showElementPreview && (
        <ElementPreviewPanel
          isOpen={showElementPreview}
          onClose={handleClosePreviewPanel}
        />
      )}
    </>
  );
};

export default Characters; 